import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from '../services/local-storage.service';

export type T_ConfigKey =
    | 'columnsPerBreakpoint'
    | 'minColumnSize'
    | 'maxBreakpoint'
    | 'breakpointIncrement'
    | 'gap'
    | 'aspectRatioWidth'
    | 'aspectRatioHeight'
    | 'homeLayoutMode'
    | 'gridLayout';

export type T_DefaultConfig = {
    columnsPerBreakpoint: number;
    minColumnSize: number;
    maxBreakpoint: number;
    breakpointIncrement: number;
    gap: number;
    aspectRatioWidth: number;
    aspectRatioHeight: number;
    homeLayoutMode: E_HomeLayoutMode;
    gridLayout: I_GridLayout;
};

@Injectable({
    providedIn: 'root'
})
export class ResponsiveService {
    private local = inject(LocalStorageService);

    private currentDisplayId: string | null = null;
    private subjects: Record<T_ConfigKey, BehaviorSubject<any>> = {} as Record<T_ConfigKey, BehaviorSubject<any>>;

    private defaultConfig: T_DefaultConfig = {
        columnsPerBreakpoint: 1,
        minColumnSize: 520,
        maxBreakpoint: 10000,
        breakpointIncrement: 800,
        gap: 20,
        aspectRatioWidth: 90,
        aspectRatioHeight: 52,
        homeLayoutMode: E_HomeLayoutMode.Fluid,
        gridLayout: { columns: 4, rows: 5 },
    };

    constructor() {
        this.initializeSubjects();
    }

    /**
     * Genera la clave del localStorage. Si hay un displayId activo y la propiedad
     * es 'homeLayoutMode' o 'gridLayout', la asocia a ese display.
     */
    private getStorageKey(key: T_ConfigKey, displayId?: string | null): string {
        if (displayId && (key === 'homeLayoutMode' || key === 'gridLayout')) {
            return `display_${displayId}_${key}`;
        }
        return key;
    }

    private initializeSubjects() {
        (Object.keys(this.defaultConfig) as T_ConfigKey[]).forEach((key) => {
            const storageKey = this.getStorageKey(key, this.currentDisplayId);
            const localValue = this.local.getItem(storageKey);
            let initialValue;

            if (key === 'homeLayoutMode' && localValue !== null) {
                let parsedValue = localValue;
                try {
                    parsedValue = JSON.parse(localValue);
                } catch { }
                const isValidMode = Object.values(E_HomeLayoutMode).includes(parsedValue as E_HomeLayoutMode);
                initialValue = isValidMode ? (parsedValue as E_HomeLayoutMode) : E_HomeLayoutMode.Fluid;
            } else {
                initialValue = localValue !== null ? JSON.parse(localValue) : this.defaultConfig[key];
            }
            if (this.subjects[key]) this.subjects[key].next(initialValue)
            else this.subjects[key] = new BehaviorSubject(initialValue);
        });
    }

    /**
     * Permite cambiar dinámicamente el display activo y recargar sus valores específicos.
     * Al emitir en los BehaviorSubjects, todos los componentes suscritos se enterarán del cambio.
     */
    setDisplayContext(displayId: string): void {
        this.currentDisplayId = displayId;
        const keysToUpdate: T_ConfigKey[] = ['homeLayoutMode', 'gridLayout'];

        keysToUpdate.forEach((key) => {
            const storageKey = this.getStorageKey(key, displayId);
            const localValue = this.local.getItem(storageKey);
            let updatedValue;

            if (key === 'homeLayoutMode' && localValue !== null) {
                let parsedValue = localValue;
                try {
                    parsedValue = JSON.parse(localValue);
                } catch { }
                const isValidMode = Object.values(E_HomeLayoutMode).includes(parsedValue as E_HomeLayoutMode);
                updatedValue = isValidMode ? (parsedValue as E_HomeLayoutMode) : E_HomeLayoutMode.Fluid;
            } else {
                updatedValue = localValue !== null ? JSON.parse(localValue) : this.defaultConfig[key];
            }

            this.subjects[key].next(updatedValue);
        });
        this.initializeSubjects();
    }

    private updateSubject(key: T_ConfigKey, value: any): void {
        this.subjects[key].next(value);
        const storageKey = this.getStorageKey(key, this.currentDisplayId);
        this.local.setItem(storageKey, JSON.stringify(value));
    }

    getSubject<T>(key: T_ConfigKey): Observable<T> {
        return this.subjects[key].asObservable();
    }

    setSubject<T>(key: T_ConfigKey, value: T): void {
        this.updateSubject(key, value);
    }

    reset() {
        (Object.keys(this.defaultConfig) as T_ConfigKey[]).forEach((key) => {
            this.subjects[key].next(this.defaultConfig[key]);
            const storageKey = this.getStorageKey(key, this.currentDisplayId);
            this.local.removeItem(storageKey);
        });
    }

    // Métodos específicos para homeLayoutMode y gridLayout
    validateHomeLayoutMode(): E_HomeLayoutMode {
        const mode = this.local.getItem('homeLayoutMode');
        const isValidMode = Object.values(E_HomeLayoutMode).includes(mode as E_HomeLayoutMode);
        return isValidMode ? (mode as E_HomeLayoutMode) : E_HomeLayoutMode.Fluid;
    }

    getHomeLayoutModeStatus(): Observable<E_HomeLayoutMode> {
        return this.getSubject<E_HomeLayoutMode>('homeLayoutMode');
    }

    setHomeLayoutModeStatus(mode: E_HomeLayoutMode): void {
        this.setSubject('homeLayoutMode', mode);
    }

    getGridLayout(): Observable<I_GridLayout> {
        return this.getSubject<I_GridLayout>('gridLayout');
    }

    setGridLayout(grid: I_GridLayout): void {
        this.setSubject('gridLayout', grid);
    }

}

export enum E_HomeLayoutMode {
    Fluid = 'fluid',
    Organic = 'organic',
    Grid = 'grid',
}


export interface I_GridLayout {
    columns: number,
    rows: number
}


